import React, { useState, useRef, useEffect } from 'react';
import { Check } from '@phosphor-icons/react';
import { useCurrency } from '@/hooks/useCurrency';

/**
 * Sidebar currency selector — sits above the light/dark toggle. Mirrors
 * ThemeToggleCompact's expanded/collapsed behaviour so it fits every shell.
 * Picking a currency updates the app-wide display currency; all values wired
 * to useCurrency().format() re-render in that currency.
 */
export const CurrencySelector = ({ expanded = false }) => {
  const { currency, setCurrency, codes, meta } = useCurrency();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  const active = meta[currency] || {};

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center gap-3 p-2 rounded-lg transition-colors" style={{ color: 'var(--muted-foreground)' }}
        data-testid="sidebar-currency-selector"
        title={`Currency: ${currency}`}
      >
        {expanded ? (
          <span className="text-sm font-medium flex items-center gap-1.5">
            {active.symbol !== currency && <span style={{ color: 'var(--foreground)' }}>{active.symbol}</span>}
            {currency}
          </span>
        ) : (
          <span className="text-[10px] font-mono font-bold">{currency}</span>
        )}
      </button>

      {open && (
        <div
          className="absolute bottom-full left-0 mb-2 z-50 w-40 border rounded-lg shadow-lg py-1 max-h-72 overflow-auto" style={{ backgroundColor: 'var(--card)', borderColor: 'var(--border)', color: 'var(--foreground)' }}
          data-testid="currency-menu"
        >
          {codes.map((code) => {
            const m = meta[code] || {};
            const isActive = code === currency;
            return (
              <button
                key={code}
                onClick={() => { setCurrency(code); setOpen(false); }}
                className={`w-full flex items-center justify-between gap-2 px-3 py-1.5 text-[13px] text-left transition-colors ${
                  isActive ? 'font-semibold' : ''
                }`}
                data-testid={`currency-option-${code}`}
              >
                <span className="flex items-center gap-2">
                 {/* Commenting out span responsible for currency symbols in dropdown 
                  <span className="w-6 text-text-muted">{m.symbol === code ? '' : ''}</span>
                  */}
                  <span>{code}</span>
                </span>
                {isActive && <Check size={13} weight="bold" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default CurrencySelector;
